/**
 * Admin API Services
 *
 * All admin-authenticated API endpoints for content management.
 */

import { get, post, put, patch, del, postFormData } from './client';

// ============================================
// Programs
// ============================================

export const programs = {
  getAll: () => get('/admin/programs'),
  create: (data) => post('/programs', data),
  update: (id, data) => put(`/programs/${id}`, data),
  delete: (id) => del(`/programs/${id}`),
};

// ============================================
// Testimonials
// ============================================

export const testimonials = {
  getAll: () => get('/testimonials'),
  create: (data) => post('/testimonials', data),
  update: (id, data) => put(`/testimonials/${id}`, data),
  delete: (id) => del(`/testimonials/${id}`),
  clearAll: () => del('/testimonials-clear-all'),
};

// ============================================
// Hero Content
// ============================================

export const heroContent = {
  get: () => get('/hero-content'),
  update: (data) => put('/hero-content', data),
};

// ============================================
// Business Info
// ============================================

export const businessInfo = {
  get: () => get('/business-info'),
  update: (data) => put('/business-info', data),
};

// ============================================
// Schedule
// ============================================

export const schedule = {
  getAll: () => get('/schedule'),
  getDay: (day) => get(`/schedule/${day}`),
  updateDay: (day, data) => put(`/schedule/${day}`, data),
  createDay: (data) => post('/schedule', data),
  clearDay: (day) => del(`/schedule/${day}`),
};

// ============================================
// Lineage
// ============================================

export const lineage = {
  getAll: () => get('/admin/lineage'),
  create: (data) => post('/lineage', data),
  update: (id, data) => put(`/lineage/${id}`, data),
  delete: (id) => del(`/lineage/${id}`),
};

// ============================================
// Gallery
// ============================================

export const gallery = {
  getAll: () => get('/admin/gallery'),
  getPublic: () => get('/gallery'),
  create: (data) => post('/gallery', data),
  update: (id, data) => put(`/gallery/${id}`, data),
  delete: (id) => del(`/gallery/${id}`),
};

// ============================================
// Navigation Links
// ============================================

export const navigation = {
  getAll: () => get('/navigation-links'),
  create: (data) => post('/admin/navigation-links', data),
  update: (id, data) => put(`/admin/navigation-links/${id}`, data),
  delete: (id) => del(`/admin/navigation-links/${id}`),
};

// ============================================
// Custom Pages
// ============================================

export const pages = {
  getAll: () => get('/admin/pages'),
  getPublic: () => get('/pages'),
  getBySlug: (slug) => get(`/pages/${slug}`),
  create: (data) => post('/admin/pages', data),
  update: (id, data) => put(`/admin/pages/${id}`, data),
  delete: (id) => del(`/admin/pages/${id}`),
  // Version history
  getVersions: (pageId) => get(`/admin/pages/${pageId}/versions`),
  getVersion: (pageId, versionId) => get(`/admin/pages/${pageId}/versions/${versionId}`),
  restore: (pageId, versionId) => post(`/admin/pages/${pageId}/restore`, { version_id: versionId }),
};

// ============================================
// Admin Users
// ============================================

export const adminUsers = {
  getAll: () => get('/admin/users'),
  getMyPreferences: () => get('/admin/users/me/preferences'),
  updateMyPreferences: (preferences) => put('/admin/users/me/preferences', preferences),
  create: (data) => post('/admin/users', data),
  update: (id, data) => put(`/admin/users/${id}`, data),
  delete: (id) => del(`/admin/users/${id}`),
  initializeProtected: () => post('/admin/initialize-protected-users', {}),
};

// ============================================
// Google Reviews
// ============================================

export const reviews = {
  fetch: () => post('/fetch-google-reviews', {}),
  convert: (reviewData) => post('/convert-google-review', reviewData),
  import: (data) => post('/import-google-reviews', data),
  getSavedForLater: () => get('/saved-for-later'),
  saveForLater: (data) => post('/save-review-for-later', data),
  convertSaved: (reviewTime) => post(`/convert-saved-review/${reviewTime}`, {}),
  removeSaved: (reviewTime) => del(`/saved-for-later/${reviewTime}`),
};

// ============================================
// File Uploads
// ============================================

export const uploads = {
  uploadImage: async (file, options = {}) => {
    const { autoGallery = true, context = null, tags = [] } = options;
    const formData = new FormData();
    formData.append('file', file);

    // Build query params for auto-gallery
    const params = new URLSearchParams();
    params.set('auto_gallery', autoGallery ? 'true' : 'false');
    if (context) params.set('context', context);
    if (tags.length > 0) params.set('tags', tags.join(','));

    const queryString = params.toString();
    const endpoint = queryString ? `/upload-image?${queryString}` : '/upload-image';

    return postFormData(endpoint, formData);
  },
  uploadResource: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return postFormData('/upload-resource', formData);
  },
  // Pre-signed URL upload (bypasses API Gateway 10MB limit)
  getResourceUploadUrl: async (filename, contentType) => {
    return post('/upload-resource-url', { filename, content_type: contentType });
  },
  completeResourceUpload: async (cloudfront_url, filename, content_type) => {
    return post('/upload-resource-complete', { cloudfront_url, filename, content_type });
  },
  importFromDrive: async (fileData) => {
    // fileData: { file_id, access_token, filename, mime_type }
    return post('/admin/import-from-drive', fileData);
  },
};

// ============================================
// Media Library
// ============================================

export const media = {
  // List with search/filter/pagination
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page);
    if (params.pageSize) queryParams.set('page_size', params.pageSize);
    if (params.search) queryParams.set('search', params.search);
    if (params.tags) queryParams.set('tags', params.tags);
    if (params.inGallery !== undefined) queryParams.set('in_gallery', params.inGallery);
    if (params.showInGallery !== undefined) queryParams.set('show_in_gallery', params.showInGallery);
    if (params.mediaType) queryParams.set('media_type', params.mediaType);
    if (params.sortBy) queryParams.set('sort_by', params.sortBy);
    if (params.sortOrder) queryParams.set('sort_order', params.sortOrder);
    const qs = queryParams.toString();
    return get(`/admin/media${qs ? `?${qs}` : ''}`);
  },

  // Get single media item
  getById: (id) => get(`/admin/media/${id}`),

  // Get all unique tags
  getTags: () => get('/admin/media/tags'),

  // Get usage locations for a media item
  getUsage: (id) => get(`/admin/media/${id}/usage`),

  // Update media item
  update: (id, data) => put(`/admin/media/${id}`, data),

  // Update tags for a media item
  updateTags: (id, tags) => put(`/admin/media/${id}/tags`, { tags }),

  // Bulk update tags
  bulkUpdateTags: (mediaIds, addTags, removeTags) =>
    post('/admin/media/bulk-tags', { media_ids: mediaIds, add_tags: addTags, remove_tags: removeTags }),

  // Delete media item (returns 409 if in use, use force=true to override)
  delete: (id, force = false) => del(`/admin/media/${id}${force ? '?force=true' : ''}`),

  // Bulk delete
  bulkDelete: (mediaIds, force = false) =>
    post(`/admin/media/bulk-delete?force=${force}`, mediaIds),

  // Scan all media for usage statistics
  scanUsage: () => post('/admin/media/scan-usage', {}),
};

// ============================================
// Subscription Settings
// ============================================

export const subscriptionSettings = {
  get: () => get('/admin/subscription-settings'),
  update: (data) => put('/admin/subscription-settings', data),
};

// ============================================
// Authorized Customers
// ============================================

export const authorizedCustomers = {
  getAll: () => get('/admin/authorized-customers'),
  create: (data) => post('/admin/authorized-customers', data),
  update: (id, data) => put(`/admin/authorized-customers/${id}`, data),
  delete: (id) => del(`/admin/authorized-customers/${id}`),
};

// ============================================
// Customers (Admin View)
// ============================================

export const customers = {
  getAll: () => get('/admin/customers'),
  getById: (id) => get(`/admin/customers/${id}`),
  update: (id, data) => put(`/admin/customers/${id}`, data),
  cancel: (id) => post(`/admin/customers/${id}/cancel`, {}),
  pause: (id) => post(`/admin/customers/${id}/pause`, {}),
  resume: (id) => post(`/admin/customers/${id}/resume`, {}),
};

// ============================================
// Safety & Privacy
// ============================================

export const safetyPrivacy = {
  get: () => get('/safety-privacy'),
  update: (data) => put('/safety-privacy', data),
};

// ============================================
// MongoDB Backups
// ============================================

export const backups = {
  // List all backups with pagination
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit);
    if (params.offset) queryParams.set('offset', params.offset);
    const qs = queryParams.toString();
    return get(`/admin/backups${qs ? `?${qs}` : ''}`);
  },

  // Get current backup/restore status (for polling)
  getStatus: () => get('/admin/backups/status'),

  // Trigger a manual backup
  trigger: () => post('/admin/backups/trigger', {}),

  // Get details for a specific backup
  getById: (backupId) => get(`/admin/backups/${backupId}`),

  // Restore from a backup (optionally specify collections to restore)
  restore: (backupId, collections = null) => {
    const body = collections ? { collections } : {};
    return post(`/admin/backups/${backupId}/restore`, body);
  },

  // Delete a backup
  delete: (backupId) => del(`/admin/backups/${backupId}`),
};

// ============================================
// Social Media Management
// ============================================

export const social = {
  // Platform Connections
  listPlatforms: () => get('/admin/social/platforms'),
  connectPlatform: (platform) => post(`/admin/social/platforms/connect/${platform}`, {}),
  disconnectPlatform: (connectionId) => del(`/admin/social/platforms/${connectionId}`),
  refreshPlatformToken: (connectionId) => post(`/admin/social/platforms/${connectionId}/refresh`, {}),
  testPlatformConnection: (connectionId) => post(`/admin/social/platforms/${connectionId}/test`, {}),

  // Assets
  listAssets: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.post_id) queryParams.set('post_id', params.post_id);
    if (params.skip) queryParams.set('skip', params.skip);
    if (params.limit) queryParams.set('limit', params.limit);
    const qs = queryParams.toString();
    return get(`/admin/social/assets${qs ? `?${qs}` : ''}`);
  },
  createAsset: (data) => post('/admin/social/assets', data),
  getAsset: (assetId) => get(`/admin/social/assets/${assetId}`),
  updateAsset: (assetId, data) => put(`/admin/social/assets/${assetId}`, data),
  deleteAsset: (assetId) => del(`/admin/social/assets/${assetId}`),
  checkAssetCompatibility: (assetId) => post(`/admin/social/assets/${assetId}/check`, {}),

  // Posts
  listPosts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.platform) queryParams.set('platform', params.platform);
    if (params.search) queryParams.set('search', params.search);
    if (params.page) queryParams.set('page', params.page);
    if (params.page_size) queryParams.set('page_size', params.page_size);
    const qs = queryParams.toString();
    return get(`/admin/social/posts${qs ? `?${qs}` : ''}`);
  },
  createPost: (data) => post('/admin/social/posts', data),
  getPost: (postId) => get(`/admin/social/posts/${postId}`),
  updatePost: (postId, data) => put(`/admin/social/posts/${postId}`, data),
  deletePost: (postId) => del(`/admin/social/posts/${postId}`),
  duplicatePost: (postId) => post(`/admin/social/posts/${postId}/duplicate`, {}),

  // Review/Approval Workflow
  submitForReview: (postId) => post(`/admin/social/posts/${postId}/submit`, {}),
  approvePost: (postId, data) => post(`/admin/social/posts/${postId}/approve`, data),
  rejectPost: (postId, data) => post(`/admin/social/posts/${postId}/reject`, data),
  getPendingPosts: () => get('/admin/social/posts/pending'),

  // Publishing
  publishPost: (postId) => post(`/admin/social/posts/${postId}/publish`, {}),
  schedulePost: (postId, data) => post(`/admin/social/posts/${postId}/schedule`, data),
  unschedulePost: (postId) => post(`/admin/social/posts/${postId}/unschedule`, {}),
  getScheduledPosts: () => get('/admin/social/posts/scheduled'),

  // Scheduler Management
  runScheduler: () => post('/admin/social/scheduler/run', {}),
  getSchedulerStatus: () => get('/admin/social/scheduler/status'),

  // Facebook Post Sync & Import
  getFacebookPosts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit);
    if (params.after) queryParams.set('after', params.after);
    const qs = queryParams.toString();
    return get(`/admin/social/facebook/posts${qs ? `?${qs}` : ''}`);
  },
  importFacebookPost: (fbPostId, title = null) => {
    const queryParams = new URLSearchParams();
    if (title) queryParams.set('title', title);
    const qs = queryParams.toString();
    return post(`/admin/social/facebook/posts/${fbPostId}/import${qs ? `?${qs}` : ''}`, {});
  },
  getPostEngagement: (postId) => get(`/admin/social/posts/${postId}/engagement`),
  syncFacebookPosts: (limit = 50) => post(`/admin/social/facebook/sync?limit=${limit}`, {}),
};

// ============================================
// Facebook Page Admin (OWZ-32)
// ============================================

export const facebook = {
  pageInfo: {
    get: () => get('/admin/facebook/page-info'),
    update: (body) => patch('/admin/facebook/page-info', body),
  },
  comments: {
    list: (opts = {}) => {
      const qs = new URLSearchParams();
      if (opts.days) qs.set('days', opts.days);
      if (opts.limit) qs.set('limit', opts.limit);
      const s = qs.toString();
      return get(`/admin/facebook/comments${s ? `?${s}` : ''}`);
    },
    reply: (commentId, message) =>
      post(`/admin/facebook/comments/${commentId}/reply`, { message }),
    hide: (commentId, hidden) =>
      post(`/admin/facebook/comments/${commentId}/hide`, { hidden }),
    delete: (commentId) => del(`/admin/facebook/comments/${commentId}`),
  },
  conversations: {
    list: (limit = 25) => get(`/admin/facebook/conversations?limit=${limit}`),
    messages: (conversationId, limit = 25) =>
      get(`/admin/facebook/conversations/${conversationId}/messages?limit=${limit}`),
    reply: (conversationId, message) =>
      post(`/admin/facebook/conversations/${conversationId}/reply`, { message }),
  },
  autoAck: {
    get: () => get('/admin/facebook/auto-ack-settings'),
    update: (enabled) =>
      put('/admin/facebook/auto-ack-settings', { auto_ack_enabled: enabled }),
  },
};

export default {
  programs,
  testimonials,
  heroContent,
  businessInfo,
  schedule,
  lineage,
  gallery,
  navigation,
  pages,
  adminUsers,
  reviews,
  uploads,
  media,
  subscriptionSettings,
  authorizedCustomers,
  customers,
  safetyPrivacy,
  backups,
  social,
};
